// src/pages/EmploymentEditPage.js
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { BsPlusCircle, BsPencilSquare, BsTrash } from "react-icons/bs";
import api from "../api";

const PERSON_ROLE_OPTIONS = [
  { value: "client", label: "Client" },
  { value: "spouse", label: "Spouse" },
];

const PAY_TYPE_OPTIONS = [
  { value: "hourly", label: "Hourly" },
  { value: "salary", label: "Salary" },
];

function toNumberOrNull(val) {
  if (val === null || val === undefined) return null;
  const s = String(val).trim();
  if (!s) return null;
  const cleaned = s.replace(/[$,]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function emptyEmployment() {
  return {
    id: null,
    person_role: "client",
    employer_name: "",
    job_title: "",
    hours_per_week: "",
    pay_rate: "",
    pay_type: "hourly",
    supervisor_name: "",
    supervisor_phone: "",
    how_long_employed: "",
    is_current: true,
  };
}

export default function EmploymentEditPage() {
  const { id } = useParams(); // client id (same as Financials)
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [items, setItems] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // employment record or null
  const [form, setForm] = useState(emptyEmployment());

  // ----------------------------
  // Smart focus refs (modal form)
  // ----------------------------
  const personRoleRef = useRef(null);
  const employerNameRef = useRef(null);
  const jobTitleRef = useRef(null);
  const hoursPerWeekRef = useRef(null);
  const isCurrentRef = useRef(null);
  const payTypeRef = useRef(null);
  const payRateRef = useRef(null);
  const howLongEmployedRef = useRef(null);
  const supervisorNameRef = useRef(null);
  const supervisorPhoneRef = useRef(null);
  const saveBtnRef = useRef(null);

  function focusRef(ref) {
    try {
      const el = ref?.current;
      if (!el) return;
      if (typeof el.focus === "function") el.focus();
      if (typeof el.select === "function") el.select();
    } catch (_) {
      // no-op
    }
  }

  // Defer focus until after Modal/DOM renders
  function deferFocus(ref) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => focusRef(ref));
    });
  }

  // ---- Load employment (do NOT block page if empty / endpoint missing) ----
  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      setError("");
      setSuccessMsg("");

      try {
        // Expected response shape: { items: [...] }
        const res = await api.get(`/clients/${id}/employment`);
        const data = res?.data || {};
        if (!isMounted) return;

        setItems(Array.isArray(data.items) ? data.items : []);
      } catch (e) {
        // IMPORTANT: mimic Financials behavior:
        // If endpoint doesn't exist yet or returns error, do NOT block the UI.
        if (!isMounted) return;
        setItems([]);
        // Optional: show a non-blocking message if you want:
        // setError(e?.response?.data?.error || e?.message || "Failed to load employment.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [id]);

  function openAdd() {
    setEditing(null);
    setForm(emptyEmployment());
    setShowModal(true);
    // actual focus occurs on Modal onEntered (below), but this is a safe fallback
    deferFocus(personRoleRef);
  }

  function openEdit(item) {
    setEditing(item);
    setForm({
      id: item.id,
      person_role: item.person_role || "client",
      employer_name: item.employer_name || "",
      job_title: item.job_title || "",
      hours_per_week: item.hours_per_week ?? "",
      pay_rate: item.pay_rate ?? "",
      pay_type: item.pay_type || "hourly",
      supervisor_name: item.supervisor_name || "",
      supervisor_phone: item.supervisor_phone || "",
      how_long_employed: item.how_long_employed || "",
      is_current: item.is_current !== false,
    });
    setShowModal(true);
    deferFocus(personRoleRef);
  }

  function closeModal() {
    if (saving) return;
    setShowModal(false);
  }

  function onChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  // "Smart focus" variants for a couple key controls (mirrors the pattern from HealthEditPage)
  function onChangePersonRole(e) {
    onChange(e);
    // After choosing person role, move to Employer Name
    deferFocus(employerNameRef);
  }

  function onChangeIsCurrent(e) {
    onChange(e);
    // After clicking current?, move to Pay Type (next control)
    deferFocus(payTypeRef);
  }

  function onChangePayType(e) {
    onChange(e);
    // After selecting pay type, jump to Pay Rate
    deferFocus(payRateRef);
  }

  async function handleSaveEmployment(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccessMsg("");

    try {
      const payload = {
        version: editing?.version,  // optimistic-lock (ignored on create)
        person_role: form.person_role,
        employer_name: form.employer_name || null,
        job_title: form.job_title || null,
        hours_per_week: toNumberOrNull(form.hours_per_week),
        pay_rate: toNumberOrNull(form.pay_rate),
        pay_type: form.pay_type || "hourly",
        supervisor_name: form.supervisor_name || null,
        supervisor_phone: form.supervisor_phone || null,
        how_long_employed: form.how_long_employed || null,
        is_current: !!form.is_current,
      };

      if (editing?.id) {
        const res = await api.put(
          `/clients/${id}/employment/${editing.id}`,
          payload
        );
        const updated = res?.data?.item;
        setItems((prev) =>
          prev.map((x) => (x.id === updated.id ? updated : x))
        );
        setSuccessMsg("Employment updated.");
      } else {
        const res = await api.post(`/clients/${id}/employment`, payload);
        const created = res?.data?.item;
        setItems((prev) => [created, ...prev]);
        setSuccessMsg("Employment added.");
      }

      setShowModal(false);
    } catch (e2) {
      if (e2?.response?.status === 409) {
        setError("This employment record was changed by someone else since you opened it. Please close, reload, and re-apply your changes.");
      } else {
        setError(
          e2?.response?.data?.error ||
            e2?.message ||
            "Failed to save employment."
        );
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item) {
    if (!item?.id) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Delete employment record for ${item.employer_name || "this employer"}?`
    );
    if (!ok) return;

    setError("");
    setSuccessMsg("");

    try {
      await api.delete(`/clients/${id}/employment/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      setSuccessMsg("Employment deleted.");
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Failed to delete employment.");
    }
  }

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading employment...
      </div>
    );
  }

  return (
    <div className="p-3">
      <Row className="mb-3">
        <Col>
          <h3 className="mb-0">Edit Employment for {form.first_name} {form.last_name}</h3>
          <div className="text-muted">Client ID: {id}</div>
        </Col>
        <Col className="d-flex justify-content-end align-items-start gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            Back
          </Button>
          <Button
            variant="outline-primary"
            type="button"
            onClick={openAdd}
            disabled={saving}
          >
            <BsPlusCircle className="me-2" />
            Add Employment
          </Button>
        </Col>
      </Row>

      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      {successMsg ? (
        <Alert variant="success" className="mb-3">
          {successMsg}
        </Alert>
      ) : null}

      <Card className="mb-3">
        <Card.Header>Employment Records</Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Person</th>
                <th>Employer</th>
                <th>Job Title</th>
                <th style={{ width: 110 }}>Pay Type</th>
                <th style={{ width: 120 }}>Pay Rate</th>
                <th style={{ width: 120 }}>Hours/Week</th>
                <th style={{ width: 90 }}>Current</th>
                <th style={{ width: 160 }} className="text-end">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-muted">
                    No employment records yet. Click <b>Add Employment</b> to create one.
                  </td>
                </tr>
              ) : (
                items.map((x) => (
                  <tr key={x.id}>
                    <td>{x.person_role === "spouse" ? "Spouse" : "Client"}</td>
                    <td>{x.employer_name || ""}</td>
                    <td>{x.job_title || ""}</td>
                    <td>{x.pay_type || ""}</td>
                    <td>{x.pay_rate != null ? Number(x.pay_rate).toFixed(2) : ""}</td>
                    <td>
                      {x.hours_per_week != null ? Number(x.hours_per_week).toFixed(2) : ""}
                    </td>
                    <td>{x.is_current ? "Yes" : "No"}</td>
                    <td className="text-end">
                      <Button
                        variant="outline-secondary"
                        type="button"
                        className="me-2"
                        onClick={() => openEdit(x)}
                        title="Edit"
                      >
                        <BsPencilSquare />
                      </Button>
                      <Button
                        variant="outline-danger"
                        type="button"
                        onClick={() => handleDelete(x)}
                        title="Delete"
                      >
                        <BsTrash />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={closeModal}
        size="lg"
        backdrop={saving ? "static" : true}
        onEntered={() => deferFocus(personRoleRef)} // focus after modal anim/render
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{editing ? "Edit Employment" : "Add Employment"}</Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSaveEmployment}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Person Role *</Form.Label>
                  <Form.Select
                    name="person_role"
                    value={form.person_role}
                    onChange={onChangePersonRole}
                    ref={personRoleRef}
                  >
                    {PERSON_ROLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={8}>
                <Form.Group>
                  <Form.Label>Employer Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="employer_name"
                    value={form.employer_name}
                    onChange={onChange}
                    ref={employerNameRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        deferFocus(jobTitleRef);
                      }
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Job Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="job_title"
                    value={form.job_title}
                    onChange={onChange}
                    ref={jobTitleRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        deferFocus(hoursPerWeekRef);
                      }
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Hours / Week</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    name="hours_per_week"
                    value={form.hours_per_week}
                    onChange={onChange}
                    placeholder="e.g. 40"
                    ref={hoursPerWeekRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        deferFocus(isCurrentRef);
                      }
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Current?</Form.Label>
                  <div className="pt-2">
                    <Form.Check
                      type="checkbox"
                      name="is_current"
                      checked={!!form.is_current}
                      onChange={onChangeIsCurrent}
                      label="Yes"
                      ref={isCurrentRef}
                    />
                  </div>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Pay Type</Form.Label>
                  <Form.Select
                    name="pay_type"
                    value={form.pay_type}
                    onChange={onChangePayType}
                    ref={payTypeRef}
                  >
                    {PAY_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Pay Rate</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    name="pay_rate"
                    value={form.pay_rate}
                    onChange={onChange}
                    placeholder="e.g. 15.50"
                    ref={payRateRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        deferFocus(howLongEmployedRef);
                      }
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>How Long Employed</Form.Label>
                  <Form.Control
                    type="text"
                    name="how_long_employed"
                    value={form.how_long_employed}
                    onChange={onChange}
                    placeholder="e.g. 6 months"
                    ref={howLongEmployedRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        deferFocus(supervisorNameRef);
                      }
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Supervisor Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="supervisor_name"
                    value={form.supervisor_name}
                    onChange={onChange}
                    ref={supervisorNameRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        deferFocus(supervisorPhoneRef);
                      }
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Supervisor Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="supervisor_phone"
                    value={form.supervisor_phone}
                    onChange={onChange}
                    ref={supervisorPhoneRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        deferFocus(saveBtnRef);
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={saving}
              ref={saveBtnRef}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
